import React, { useState, useEffect, useCallback } from "react";
import { Card, CardBody, Col } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux-toolkit/store";
import { getData } from "../../../CommonElements/Functions/PostGetFun";
import { API } from "../../../api/API";
import { getFilteredPositionIds } from "../../../db/DexieServises";
import { setOutletTypes } from "../../../redux-toolkit/reducers/OutletTypeSlice";

interface EffortReportItem {
  PrimaryDcrAverage: string;
  SecondaryCallAvg: string;
}

const DailyCallAverage: React.FC = () => {
  const [chartSeries, setChartSeries] = useState<{ name: string; data: number[] }[]>([]);
  const [chartOptions, setChartOptions] = useState<any>({});
  const [monthsList, setMonthsList] = useState<string[]>([]);

  const dispatch = useDispatch();
  const positions = useSelector((state: RootState) => state.position.positions);
  const dateRange = useSelector((state: RootState) => state.position.dateRange);
  const unitId = useSelector((state: RootState) => state.orgUnit.id);

  const primaryOutletType = useSelector((state: RootState) => state.outletType.primaryOutletType);
  const secondaryOutletType = useSelector((state: RootState) => state.outletType.secondaryOutletType);
  const primaryOutletName = useSelector((state: RootState) => state.outletTypeName.OutlettypeNamePrimary);
  const secondaryOutletName = useSelector((state: RootState) => state.outletTypeName.OutlettypeNameSecondary);

  /**
   * Converts "MMM-yyyy" to "yyyy-MM" format
   */
  const formatMonth = (month: string): string => {
    const [monthName, year] = month.split("-");
    const monthNumber = new Date(`${monthName} 1, ${year}`).getMonth() + 1;
    return `${year}-${monthNumber.toString().padStart(2, "0")}`;
  };

  /**
   * Fetch Effort Report data for given positions and month
   */
  const fetchEffortReport = useCallback(
    async (positionIds: string, month: string): Promise<EffortReportItem[]> => {
      const serverUrl = localStorage.getItem("serverUrl");
      if (!serverUrl) {
        console.error("Server URL not found in localStorage.");
        return [];
      }

      const url = `${serverUrl}${API.getEffortReport}`;
      console.log(month, positionIds, '=======================month param---', dateRange);

      try {
        const { status, data } = await getData(url, true, {
          position_id: positionIds,
          moye: month,
        });

        if (status === 200 && Array.isArray(data.data)) {
          return data.data;
        } else {
          console.error("Failed to fetch effort report data");
          return [];
        }
      } catch (error) {
        console.error("Error fetching effort report:", error);
        return [];
      }
    },
    [dateRange]
  );

  /**
   * Process API data and prepare chart values
   */
  const processChartData = useCallback(async () => {
    const positionIds =
      positions.length > 0
        ? positions.map((pos) => pos.id).join(",")
        : (await getFilteredPositionIds("Position", unitId)).join(",");

    const monthsToUse = monthsList.length > 0 ? monthsList : [];

    const chartData = { primary: [] as number[], secondary: [] as number[] };

    for (const month of monthsToUse) {
      const apiData = await fetchEffortReport(positionIds, month);

      const avgPrimary =
        apiData.length > 0
          ? apiData.reduce((sum, item) => sum + parseFloat(item.PrimaryDcrAverage || "0"), 0) /
            apiData.length
          : 0;

      const avgSecondary =
        apiData.length > 0
          ? apiData.reduce((sum, item) => sum + parseFloat(item.SecondaryCallAvg || "0"), 0) /
            apiData.length
          : 0;

      chartData.primary.push(avgPrimary);
      chartData.secondary.push(avgSecondary);
    }

    setChartSeries([
      { name: primaryOutletName || "Primary Outlet", data: chartData.primary },
      { name: secondaryOutletName || "Secondary Outlet", data: chartData.secondary },
    ]);

    setChartOptions({
      chart: {
        type: "line",
        height: 400,
        toolbar: {
          tools: {
            download: true,
            zoom: true,
            zoomin: true,
            zoomout: true,
            pan: true,
            reset: true,
          },
        },
      },
      title: { text: "Calls Average (Monthly)" },
      xaxis: {
        categories: monthsToUse,
        title: { text: "Month" },
      },
      yaxis: {
        title: { text: "Number of Calls" },
        min: 0,
        max: 10,
        labels: { formatter: (val: number) => Math.round(val).toString() },
      },
      tooltip: {
        y: { formatter: (val: number) => Math.round(val).toString() },
      },
      colors: ["#4285F4", "#DB4437"],
      legend: { position: "top" },
    });
  }, [positions, unitId, monthsList, primaryOutletName, secondaryOutletName, fetchEffortReport]);

  /**
   * Builds month list from selected date range
   */
  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      const startMonth = parseInt(dateRange.start.split("-")[0], 10);
      const startYear = parseInt(dateRange.start.split("-")[1], 10);
      const endMonth = parseInt(dateRange.end.split("-")[0], 10);
      const endYear = parseInt(dateRange.end.split("-")[1], 10);

      const months: string[] = [];

      for (let year = startYear; year <= endYear; year++) {
        const monthStart = year === startYear ? startMonth : 1;
        const monthEnd = year === endYear ? endMonth : 12;

        for (let month = monthStart; month <= monthEnd; month++) {
          const formattedMonth = `${year}-${month.toString().padStart(2, "0")}`;
          months.push(formattedMonth);
        }
      }

      setMonthsList(months);
    } else {
      // Default to all months (01 to 12) for the current year if dateRange is not selected
      const currentYear = new Date().getFullYear();
      const allMonths = Array.from({ length: 12 }, (_, i) => `${currentYear}-${(i + 1).toString().padStart(2, "0")}`);
      setMonthsList(allMonths);
    }
  }, [dateRange]);

  /**
   * Recalculate chart whenever dependencies change
   */
  useEffect(() => {
    if (positions.length > 0 && dateRange.start && dateRange.end) {
      processChartData();
    }
  }, [positions, dateRange, processChartData]);

  /**
   * Re-render chart when outlet names change
   */
  useEffect(() => {
    processChartData();
  }, [primaryOutletName, secondaryOutletName, processChartData]);

  return (
    <Col className="col-sm-12 col-xl-6 box-col-6">
      <Card>
        <CardBody>
          <ReactApexChart options={chartOptions} series={chartSeries} type="line" height={400} />
        </CardBody>
      </Card>
    </Col>
  );
};

export default DailyCallAverage;
